/**
 * Drag-to-reorder the view's columns (spec-column level — in wide mode a field is
 * one draggable unit, not its per-instance sub-columns). Uses @dnd-kit.
 */
import { ArrowUpDown, GripVertical } from 'lucide-react'
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@/components/ui/button'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import type { ViewColumn } from '../types/data-view-spec'

interface ColumnReorderProps {
  columns: ViewColumn[]
  labelOf: (col: ViewColumn) => string
  onChange: (cols: ViewColumn[]) => void
}

export function ColumnReorder({ columns, labelOf, onChange }: ColumnReorderProps) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) return
    const from = columns.findIndex((c) => c.id === active.id)
    const to = columns.findIndex((c) => c.id === over.id)
    if (from < 0 || to < 0) return
    onChange(arrayMove(columns, from, to))
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <ArrowUpDown className="w-3.5 h-3.5" />ترتیب
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 max-h-[70vh] overflow-y-auto p-2">
        <p className="text-xs text-muted-foreground px-1 pb-1.5">بکشید تا ترتیب ستون‌ها تغییر کند</p>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={columns.map((c) => c.id)} strategy={verticalListSortingStrategy}>
            {columns.map((col) => (
              <SortableRow key={col.id} id={col.id} label={labelOf(col)} />
            ))}
          </SortableContext>
        </DndContext>
      </PopoverContent>
    </Popover>
  )
}

function SortableRow({ id, label }: { id: string; label: string }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm bg-card ${isDragging ? 'opacity-60 shadow' : ''}`}
      {...attributes}
      {...listeners}
    >
      <GripVertical className="w-3.5 h-3.5 text-muted-foreground cursor-grab shrink-0" />
      <span className="flex-1 truncate">{label}</span>
    </div>
  )
}
